import React, { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import { deleteVal, getVal, setVal } from "oolib";
import { useQueryClient } from "react-query";
import Moment from "moment";

import { useGetAllDateFieldOptions } from "../utils";
import { useGetAllOptionsForDimension1 } from "../../WidgetBarChart/utils/useGetAllOptionsForDimension1";
import { getDerivedTimeSeriesTitle } from "../../WidgetBarChart/utils/_extractWidgetTitle";
import { getWidgetContentTypes, pruneStaleWidgetFilters } from "../../utils/getWidgetContentTypes";
import { useReportLandingContext } from "../../../../ReportLandingContext";
import { getIntervalOptionsForTimeRange, calculateDataDateRange } from "../utils/timeRangeUtils";
import { useArithmeticExpression } from "../../utils/ArithmeticExpressionInput/useArithmeticExpression";
import { DATA_KEYS } from "./DATA_KEYS";

const LineChartConfigContext = createContext(null);

export const useLineChartConfig = () => {
  const ctx = useContext(LineChartConfigContext);
  if (!ctx) throw new Error("useLineChartConfig must be used within LineChartConfigProvider");
  return ctx;
};

export const LineChartConfigProvider = ({
  thisWidgetConfig,
  setConfig,
  underEditWidgetIdx,
  children,
}) => {
  const queryClient = useQueryClient();
  const allTpls = queryClient.getQueryData("allTpls");
  const { widgetTimeRangeOptions, widgetDataStore } = useReportLandingContext();

  // --- updateWidget helper ---
  const updateWidget = useCallback((updaterFn) => {
    setConfig((prev) => {
      let widgetConfig = JSON.parse(JSON.stringify(prev[underEditWidgetIdx]));
      const widgetType = widgetConfig?.comp || 'WidgetLineChart';
      const prevContentTypes = getWidgetContentTypes(widgetConfig, widgetType);
      widgetConfig = updaterFn(widgetConfig);
      // A write that changes which datasets the widget reads drops the widget
      // filters that belonged to a dataset it left (see pruneStaleWidgetFilters).
      widgetConfig = pruneStaleWidgetFilters(widgetConfig, prevContentTypes, widgetType);
      const fullConfig = JSON.parse(JSON.stringify(prev));
      fullConfig[underEditWidgetIdx] = widgetConfig;
      return fullConfig;
    });
  }, [setConfig, underEditWidgetIdx]);

  // --- Time range options & data span ---
  const widgetTimeRangeData = widgetTimeRangeOptions?.[underEditWidgetIdx];
  const timeRangeOptions = widgetTimeRangeData?.options || [{ display: "All Data", value: "all" }];
  const dataSpanDays = widgetTimeRangeData?.dataSpanDays || 365;

  // --- Derived values from config ---
  const selectedTimeRange = getVal(thisWidgetConfig, DATA_KEYS.selectedTimeRange) || { display: "All Data", value: "all" };
  const cyclicalValue = getVal(thisWidgetConfig, DATA_KEYS.cyclical) || { display: "No", value: false };

  // --- Data date bounds (from chart data) ---
  const widgetId = getVal(thisWidgetConfig, "props.id") || thisWidgetConfig?.props?.id || thisWidgetConfig?.id;
  const chartDataForBounds = widgetId ? widgetDataStore?.[widgetId] : null;
  const dataDateRange = useMemo(() => {
    return calculateDataDateRange(chartDataForBounds, "timeInterval");
  }, [chartDataForBounds]);

  const dataBounds = useMemo(() => {
    if (!dataDateRange?.startDate || !dataDateRange?.endDate) return null;
    return {
      start: dataDateRange.startDate.toISOString(),
      end: dataDateRange.endDate.toISOString(),
    };
  }, [dataDateRange]);

  // --- Restricted interval options ---
  const restrictedIntervalOptions = useMemo(() => {
    const baseOptions = getIntervalOptionsForTimeRange(selectedTimeRange?.value, dataSpanDays);
    if (cyclicalValue?.value === true) {
      const restricted = baseOptions.filter(opt => opt.value === 'month');
      return restricted.length > 0 ? restricted : baseOptions;
    }
    return baseOptions;
  }, [selectedTimeRange?.value, dataSpanDays, cyclicalValue?.value]);

  // --- Auto-validate interval when restrictions change ---
  useEffect(() => {
    if (!restrictedIntervalOptions || restrictedIntervalOptions.length === 0) return;
    const currentInterval = getVal(thisWidgetConfig, DATA_KEYS.interval);
    const isCurrentIntervalValid = restrictedIntervalOptions.some(opt => opt.value === currentInterval?.value);
    if (isCurrentIntervalValid) return;
    updateWidget(w => setVal(w, DATA_KEYS.interval, restrictedIntervalOptions[0]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restrictedIntervalOptions, underEditWidgetIdx]);

  // --- Options ---
  const { options: dateFieldOptions } = useGetAllDateFieldOptions();

  const selectedDateField = getVal(thisWidgetConfig, DATA_KEYS.dateField);
  const selectedContentType = selectedDateField?.contentType;

  const { options: allDimensionOptions } = useGetAllOptionsForDimension1({
    showNumberInputsInOptions: true,
  });

  const dimensionOptions = useMemo(() => {
    if (!selectedContentType || !allDimensionOptions) {
      return allDimensionOptions;
    }
    return allDimensionOptions.map(group => {
      if (group.display === 'Annotation Tags Based') {
        // Annotation dimensions (tags, sub-themes and drills, topics) go over
        // as the annotations collection bucketed on this dataset's date —
        // see TimeSeriesWidget_processRawConfigFn.
        return group;
      }
      if (group.display === (allTpls?.find(tpl => tpl.kp_content_type === selectedContentType)?.general?.content?.title || selectedContentType)) {
        return group;
      }
      return null;
    }).filter(Boolean);
  }, [selectedContentType, allDimensionOptions, allTpls]);

  const widgetType = thisWidgetConfig?.comp || 'WidgetLineChart';
  const contentTypesForFilters = useMemo(() => {
    return getWidgetContentTypes(thisWidgetConfig, widgetType);
  }, [thisWidgetConfig, widgetType]);

  // --- Handlers ---
  const handleDateFieldChange = useCallback((k, v) => {
    updateWidget((w) => {
      w = setVal(w, DATA_KEYS.dateField, v);
      w = deleteVal(w, DATA_KEYS.dimension);
      const hasCustomTitle = getVal(w, 'props.hasCustomTitle');
      if (!hasCustomTitle) {
        w = setVal(w, DATA_KEYS.field1, v?.display || '');
      }
      return w;
    });
  }, [updateWidget]);

  const handleTimeRangeChange = useCallback((k, v) => {
    updateWidget((w) => {
      w = setVal(w, DATA_KEYS.selectedTimeRange, v);

      // Auto-update interval to first valid option for new time range
      const baseIntervalOptions = getIntervalOptionsForTimeRange(v?.value, dataSpanDays);
      const newIntervalOptions = (getVal(w, DATA_KEYS.cyclical)?.value === true)
        ? (baseIntervalOptions.filter(opt => opt.value === 'month').length > 0
          ? baseIntervalOptions.filter(opt => opt.value === 'month')
          : baseIntervalOptions)
        : baseIntervalOptions;

      const currentInterval = getVal(w, DATA_KEYS.interval);
      const isCurrentIntervalValid = newIntervalOptions.some(opt => opt.value === currentInterval?.value);
      if (!isCurrentIntervalValid && newIntervalOptions.length > 0) {
        w = setVal(w, DATA_KEYS.interval, newIntervalOptions[0]);
      }

      // If switching to Custom, initialize customTimeRange to bounds if missing
      if (v?.value === "custom" && dataBounds?.start && dataBounds?.end) {
        const existingCustom = getVal(w, DATA_KEYS.customTimeRange);
        const hasStart = Boolean(existingCustom?.start);
        const hasEnd = Boolean(existingCustom?.end);
        if (!hasStart || !hasEnd) {
          w = setVal(w, DATA_KEYS.customTimeRange, {
            start: dataBounds.start,
            end: dataBounds.end,
          });
        }
      }

      return w;
    });
  }, [updateWidget, dataSpanDays, dataBounds]);

  const handleCustomDateRangeChange = useCallback((k, v) => {
    const clampIsoToBounds = (value) => {
      if (!value) return null;
      const m = Moment(value instanceof Date ? value : value);
      if (!m.isValid()) return null;
      if (!dataBounds?.start || !dataBounds?.end) return m.toISOString();
      const min = Moment(dataBounds.start);
      const max = Moment(dataBounds.end);
      return Moment.min(Moment.max(m, min), max).toISOString();
    };

    const rawStart = Array.isArray(v) ? v[0] : null;
    const rawEnd = Array.isArray(v) ? v[1] : null;
    const nextStart = clampIsoToBounds(rawStart);
    const nextEnd = clampIsoToBounds(rawEnd);

    updateWidget(w => setVal(w, DATA_KEYS.customTimeRange, { start: nextStart, end: nextEnd }));
  }, [updateWidget, dataBounds]);

  const handleCyclicalChange = useCallback((k, v) => {
    updateWidget(w => setVal(w, DATA_KEYS.cyclical, v));
  }, [updateWidget]);

  const handleIntervalChange = useCallback((k, v) => {
    updateWidget(w => setVal(w, DATA_KEYS.interval, v));
  }, [updateWidget]);

  const handleDimensionChange = useCallback((v) => {
    updateWidget((w) => {
      w = setVal(w, DATA_KEYS.dimension, v);
      const hasCustomTitle = getVal(w, 'props.hasCustomTitle');
      if (!hasCustomTitle) {
        const dateField = getVal(w, DATA_KEYS.dateField);
        const interval = getVal(w, DATA_KEYS.interval);
        const newTitle = getDerivedTimeSeriesTitle({ dateField, dimension: v, interval });
        if (newTitle) {
          w = setVal(w, DATA_KEYS.field1, newTitle);
        }
      }
      return w;
    });
  }, [updateWidget]);

  // --- Arithmetic expression state (shared, same pattern as BarChart/MetricCard) ---
  const dimensionValue = getVal(thisWidgetConfig, DATA_KEYS.dimension);

  const arithmeticState = useArithmeticExpression({
    allOptions: dimensionOptions,
    value: dimensionValue,
    onChange: handleDimensionChange,
    disabled: false,
  });

  // --- Context value ---
  const value = useMemo(() => ({
    widgetConfig: thisWidgetConfig,
    updateWidget,
    dataKeys: DATA_KEYS,
    setConfig,
    underEditWidgetIdx,
    // Options
    dateFieldOptions,
    dimensionOptions,
    timeRangeOptions,
    restrictedIntervalOptions,
    contentTypesForFilters,
    // Derived
    dataBounds,
    dataSpanDays,
    selectedTimeRange,
    cyclicalValue,
    allTpls,
    // Arithmetic
    arithmeticState,
    // Handlers
    handleDateFieldChange,
    handleTimeRangeChange,
    handleCustomDateRangeChange,
    handleCyclicalChange,
    handleIntervalChange,
    handleDimensionChange,
  }), [
    thisWidgetConfig, updateWidget, setConfig, underEditWidgetIdx,
    dateFieldOptions, dimensionOptions, timeRangeOptions, restrictedIntervalOptions,
    contentTypesForFilters, dataBounds, dataSpanDays, selectedTimeRange, cyclicalValue, allTpls,
    arithmeticState,
    handleDateFieldChange, handleTimeRangeChange, handleCustomDateRangeChange,
    handleCyclicalChange, handleIntervalChange, handleDimensionChange,
  ]);

  return (
    <LineChartConfigContext.Provider value={value}>
      {children}
    </LineChartConfigContext.Provider>
  );
};
